/**
 * V2 admin · Properties list.
 *
 * Renders two distinct layouts depending on the current user's role:
 *
 *  • Owner portal (isOwnerPortalOnly = true):
 *      Clean "My properties" grid matching the owner-portal design —
 *      diagonal-stripe cards, ROOMS / OCCUPANCY / YIELD stats, overdue
 *      badge, "Submit a property" CTA. Yield data comes from the
 *      owner_portfolio_dashboard RPC so numbers match the Analytics tab.
 *
 *  • Staff / admin:
 *      Full-featured list with stats strip, city chip filter, grid/list
 *      view toggle, pagination, and "Add property" / "Manage lessors" CTAs.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building,
  Building2,
  CheckCircle2,
  BedDouble,
  Home,
  Plus,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useOwnerPortalV3 } from "@/hooks/owner/useOwnerPortalV3";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { PAGE_TITLE_CLASS } from "@/components/admin/v2/page-title";
import { PropertyCard } from "@/components/admin/v2/PropertyCard";
import { OwnerPropertyCard } from "@/components/admin/v2/OwnerPropertyCard";
import { PendingPropertyCard } from "@/components/admin/v2/PendingPropertyCard";
import { PropertyListRow } from "@/components/admin/v2/PropertyListRow";
import { PropertyApprovalsRail } from "@/components/admin/v2/approvals/PropertyApprovalsRail";
import { PropertyGlobalSearch } from "@/components/admin/v2/property-tabs/PropertyGlobalSearch";
import { EmptyState } from "@/components/admin/v2/primitives/EmptyState";
import { FilterChipBar } from "@/components/admin/v2/primitives/FilterChipBar";
import { StatCard } from "@/components/admin/v2/primitives/StatCard";
import { ViewToggle } from "@/components/admin/v2/primitives/ViewToggle";
import { useViewMode } from "@/components/admin/v2/primitives/useViewMode";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useLessors } from "@/hooks/v2/useLessors";
import { useAdminPropertySubmissions } from "@/hooks/v2/usePropertySubmissions";
import {
  useOwnerVisiblePropertyStates,
  ownerMustSign,
  ownerPropertyIsLive,
  ownerOnboardingInReview,
  type OwnerPropertyAxes,
  useProperties,
} from "@/hooks/v2/useProperties";
import { useGlobalSearchIndex } from "@/hooks/v2/useGlobalSearchIndex";
import { useAllPropertiesRentPaidStats } from "@/hooks/v2/usePropertyRentPaidStats";
import { useAllPropertiesReminderCounts } from "@/hooks/v2/usePropertyReminderStats";
import type { ActiveOccupant } from "@/hooks/v2/useReassignBookings";
import type { PropertyListItem } from "@/types/property";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

// ── Types from the owner_portfolio_dashboard RPC ──────────────────────────
type PortfolioRow = {
  property_id: number;
  yield_pct: number | null;
  revenue_period_cents: number;
  occupancy_pct: number;
  occupied_rooms: number;
  total_rooms: number;
};
type PortfolioPayload = {
  properties: PortfolioRow[];
};

/**
 * A property with nothing left to manage — either the whole-property
 * `is_hidden` flag, or every one of its apartments hidden (COL-463) — sinks
 * to the very bottom of the grid, below even a property with outstanding
 * rent. `all_apartments_hidden` is only ever true when the property has at
 * least one apartment; a plain WG building with none is never sunk by this.
 */
function isSunkToBottom(p: PropertyListItem): boolean {
  return p.is_hidden || p.all_apartments_hidden;
}

/**
 * Where a property sits in the owner's "My properties" grid: what the owner
 * has to do first, then what is waiting on coliviq, then what is done.
 *
 *   0  needs the owner — a management contract to sign, or a submission coliviq
 *      sent back for changes
 *   1  pending review — contract documents or a submission being checked
 *   2  approved and live
 *
 * Read from the two axes, not from a combined state: "must sign" and
 * "refused" are different questions (see OwnerPropertyAxes).
 */
export function ownerActionRank(
  axes: OwnerPropertyAxes | undefined
): 0 | 1 | 2 {
  if (ownerMustSign(axes) || axes?.approval === "rejected") return 0;
  return ownerPropertyIsLive(axes) ? 2 : 1;
}

/** Owner grid order: by action (see `ownerActionRank`), then newest first. */
export function compareOwnerPropertiesByAction(
  a: PropertyListItem,
  b: PropertyListItem,
  axesOf: (p: PropertyListItem) => OwnerPropertyAxes | undefined
): number {
  const rankA = ownerActionRank(axesOf(a));
  const rankB = ownerActionRank(axesOf(b));
  if (rankA !== rankB) return rankA - rankB;
  return (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0);
}

/**
 * Comparator for the admin properties grid: properties sunk to the bottom
 * (COL-463) sort last as a whole group; within each group, most overdue
 * reminders first, then highest outstanding-rent % (COL-456). Overdue
 * reminders outrank rent collected: a property with reminders waiting on
 * staff is the more urgent one, whatever its % paid. A property with no
 * overdue reminders (or whose counts haven't loaded — `overdueCountById`
 * omitted/missing the id) counts as 0, so it ranks purely by rent. A property
 * with no active tenant, or nothing outstanding this month, has no entry/`null`
 * in `outstandingPctById` and falls back to the prior created_at-desc order,
 * which also breaks ties between two properties equal on both counts.
 */
export function compareByOverdueThenOutstandingRent(
  a: PropertyListItem,
  b: PropertyListItem,
  outstandingPctById: Map<number, number | null>,
  overdueCountById: Map<number, number> = new Map()
): number {
  const sunkA = isSunkToBottom(a);
  const sunkB = isSunkToBottom(b);
  if (sunkA !== sunkB) return sunkA ? 1 : -1;

  const overdueA = overdueCountById.get(a.id) ?? 0;
  const overdueB = overdueCountById.get(b.id) ?? 0;
  if (overdueA !== overdueB) return overdueB - overdueA;

  const outstandingA = outstandingPctById.get(a.id) ?? null;
  const outstandingB = outstandingPctById.get(b.id) ?? null;
  if (outstandingA != null && outstandingB != null) {
    if (outstandingA !== outstandingB) return outstandingB - outstandingA;
  } else if ((outstandingA != null) !== (outstandingB != null)) {
    return outstandingA != null ? -1 : 1;
  }
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

/**
 * Property ids whose apartments, rooms, or assigned tenant (name/email) match
 * `term` — used so a nested match keeps the parent property visible in the
 * list even though the property's own title/address/city didn't match.
 * Reuses the same portfolio-wide index the property-detail global search box
 * builds, rather than a second query/join.
 */
function useNestedMatchPropertyIds(
  searchIndex: ReturnType<typeof useGlobalSearchIndex>,
  term: string
): Set<string> | null {
  return useMemo(() => {
    if (!term) return null;
    // Stringify every id before inserting/looking up. properties.id and the
    // apartments/rooms/bookings *_id columns are all bigint, and PostgREST
    // doesn't consistently narrow them to JS numbers across separate
    // queries — see useProperties.ts's roomsByProp comment for the same
    // observed variance. A raw Set<number>/Map<number,_> silently drops
    // every match the moment one query returns a stringified id.
    const occupantsByRoomId = new Map<string, ActiveOccupant>();
    for (const [roomId, occ] of searchIndex.occupants) {
      occupantsByRoomId.set(String(roomId), occ);
    }
    const ids = new Set<string>();
    for (const a of searchIndex.apartments) {
      if ((a.label ?? "").toLowerCase().includes(term))
        ids.add(String(a.property_id));
    }
    for (const r of searchIndex.rooms) {
      if ((r.title ?? "").toLowerCase().includes(term)) {
        ids.add(String(r.property_id));
        continue;
      }
      const occ = occupantsByRoomId.get(String(r.id));
      if (!occ) continue;
      if (
        (occ.name ?? "").toLowerCase().includes(term) ||
        (occ.email ?? "").toLowerCase().includes(term)
      ) {
        ids.add(String(r.property_id));
      }
    }
    return ids;
  }, [searchIndex, term]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Owner portal view
// ─────────────────────────────────────────────────────────────────────────────
function OwnerPropertiesView() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const v3 = useOwnerPortalV3();

  // Gated on a known user, like the sidebar's copy of this query. Without it
  // `useProperties(undefined)` runs while auth hydrates and fetches EVERY
  // property in the database -- a full-table read for nothing, and a
  // non-empty result that hides the glow until `user` arrives, so this page
  // and the sidebar disagree for that beat.
  //
  // `user?.id` alone is the gate: this view only renders for owner-portal
  // accounts (see the router at the bottom of the file), so the sidebar's
  // extra `isOwnerPortalOnly &&` would be redundant here.
  const {
    data,
    isLoading: propertiesLoading,
    isSuccess,
  } = useProperties(user?.id, !!user?.id);
  // Only pull the portfolio-wide apartments/rooms/tenants index once the
  // operator actually types a search term, so a plain page load stays a
  // single `useProperties()` fetch.
  const searchIndex = useGlobalSearchIndex(search.trim().length > 0);
  const nestedMatchIds = useNestedMatchPropertyIds(
    searchIndex,
    search.trim().toLowerCase()
  );

  /**
   * Onboarding axes per property. Every property the owner holds is listed —
   * the ones that aren't onboarded yet render blurred rather than being hidden,
   * which is what used to happen and is why an owner could open their portal
   * and not find a building they own.
   *
   * Missing entries default to "live": the id set and the axes come from the
   * same RPC, so an absent entry simply hasn't resolved yet, and a card that
   * flashes blurred before settling reads as a bug.
   *
   * The grid stays on its skeleton until the axes have arrived: they decide
   * the card ORDER (see compareOwnerPropertiesByAction) and the header counts,
   * so rendering before them would show a recency-sorted grid that then
   * reshuffles every card.
   */
  const { data: stateByPropertyId, isPending: statesPending } =
    useOwnerVisiblePropertyStates();
  const isLoading = propertiesLoading || statesPending;
  /** The two axes for one property; every decision below reads these. */
  const axesOf = useCallback(
    (p: PropertyListItem) => stateByPropertyId?.get(Number(p.id)),
    [stateByPropertyId]
  );
  const isLive = useCallback(
    (p: PropertyListItem) => ownerPropertyIsLive(axesOf(p)),
    [axesOf]
  );

  // Fetch yield per property from the same RPC used by Analytics tab
  const { data: portfolioData } = useQuery({
    queryKey: ["owner-portfolio-dashboard", "month"],
    queryFn: async () => {
      const { data: raw, error } = await supabase.rpc(
        "owner_portfolio_dashboard",
        { _revenue_range: "month" }
      );
      if (error) return null;
      return raw as PortfolioPayload | null;
    },
    staleTime: 60_000,
  });

  // Build a map of property_id → yield_pct for fast lookup
  const yieldByPropertyId = useMemo(() => {
    const m = new Map<number, number | null>();
    for (const row of portfolioData?.properties ?? []) {
      m.set(row.property_id, row.yield_pct);
    }
    return m;
  }, [portfolioData]);

  const all = useMemo(() => data ?? [], [data]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return all;
    return all.filter(
      (p) =>
        (p.title ?? "").toLowerCase().includes(term) ||
        (p.address ?? "").toLowerCase().includes(term) ||
        (p.city ?? "").toLowerCase().includes(term) ||
        (nestedMatchIds?.has(String(p.id)) ?? false)
    );
  }, [all, search, nestedMatchIds]);

  // Action first, then recency: what needs the owner's signature, then what is
  // waiting on coliviq, then what is approved and live.
  const ordered = useMemo(
    () =>
      [...filtered].sort((a, b) =>
        compareOwnerPropertiesByAction(a, b, axesOf)
      ),
    [filtered, axesOf]
  );
  // Counts describe the whole portfolio, not the current search.
  const liveCount = useMemo(
    () => all.filter((p) => isLive(p)).length,
    [all, isLive]
  );
  const notOnboardedCount = all.length - liveCount;

  return (
    <AdminV2Layout>
      {/* ── Header row ─────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className={PAGE_TITLE_CLASS}>
            {/* Same key as the sidebar entry, so the page title can never read
                differently from the nav label that led here. */}
            {t("admin.nav.myProperties", "My properties")}
          </h1>
          {!isLoading && (
            <p className="mt-1.5 text-sm text-muted-foreground">
              {liveCount}{" "}
              {liveCount === 1
                ? t("owner.properties.activeCountOne", "active")
                : t("owner.properties.activeCountMany", "active")}
              {notOnboardedCount > 0 && (
                <>
                  {" · "}
                  {notOnboardedCount}{" "}
                  {v3
                    ? t(
                        "owner.properties.onboardingPendingCount",
                        "onboarding pending"
                      )
                    : t(
                        "owner.properties.notOnboardedCount",
                        "yet to be onboarded"
                      )}
                </>
              )}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:justify-end sm:ml-auto shrink-0">
          {/* Same categorized apartment/room/tenant/property dropdown as the
              admin properties list and property-detail pages. Tenant
              matches stay super-admin-gated (inherited from
              useGlobalSearchIndex); an owner-portal caller can't see them
              anyway since it's never a super admin. */}
          <div className="relative flex-1 min-w-[11rem] sm:flex-initial sm:w-56 lg:w-64">
            <PropertyGlobalSearch
              showPropertyGroup
              value={search}
              onDebouncedChange={setSearch}
              placeholder={t(
                "owner.properties.searchPlaceholder",
                "Search properties"
              )}
              inputId="owner-properties-search"
            />
          </div>

          {/* Add a property CTA — opens the wizard, submission goes to admin review.
              Glows while the owner has none at all (Neel, 2026-10-07): the
              sidebar glow brings them to this tab, and this is the next step.
              `all` is this owner's full unfiltered set, so a search that
              matches nothing does not re-light it. */}
          <button
            type="button"
            onClick={() => navigate("/admin/properties/new")}
            className={`inline-flex items-center gap-2 rounded-xl bg-petrol hover:bg-petrol-darker text-white text-sm font-semibold px-4 py-2 transition-colors shadow-sm ${
              isSuccess && all.length === 0 ? "owner-cta-glow" : ""
            }`}
          >
            <Plus className="h-4 w-4" />
            {t("owner.properties.addProperty", "Add a property")}
          </button>
        </div>
      </div>

      {/* ── Skeleton loading ─────────────────────────────────────────── */}
      {isLoading && (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl overflow-hidden border border-gray-200 bg-white"
            >
              <Skeleton className="h-36 w-full rounded-none" />
              <div className="p-4 space-y-3">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <div className="flex gap-5 pt-1">
                  <Skeleton className="h-8 w-12" />
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-8 w-12" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state ──────────────────────────────────────────────── */}
      {!isLoading && all.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Building2 className="h-12 w-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">
            {t("owner.properties.emptyTitle", "No properties yet")}
          </h3>
          <p className="text-sm text-gray-500">
            {t(
              "owner.properties.emptyDesc",
              "Add your first property, or contact us to link an existing one to this portal."
            )}
          </p>
        </div>
      )}

      {/* ── No search results ────────────────────────────────────────── */}
      {!isLoading && all.length > 0 && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-sm text-gray-500">
            {t("owner.properties.noMatch", "No properties match")} &ldquo;
            {search}&rdquo;.
          </p>
          <button
            onClick={() => setSearch("")}
            className="mt-2 text-sm text-[#2dd4bf] hover:underline"
          >
            {t("owner.properties.clearSearch", "Clear search")}
          </button>
        </div>
      )}

      {/* ── Property grid ────────────────────────────────────────────── */}
      {!isLoading && filtered.length > 0 && (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {ordered.map((p) =>
            isLive(p) ? (
              <OwnerPropertyCard
                key={p.id}
                property={p}
                yieldPct={yieldByPropertyId.get(p.id) ?? null}
              />
            ) : /* A submission the owner can still edit or withdraw keeps its own
               card — the actions belong to it. Everything else is a real
               property waiting on onboarding, shown blurred behind the tag.

               Keyed on approval_status, NOT on the derived axes: since the
               two-axis port a submitted property is asked to onboard BEFORE
               review, so it can report onboarding "outstanding" while still
               being a submission. Choosing the card by onboarding state would
               hand it to OwnerPropertyCard and take Edit/Withdraw — and, when
               rejected, the review note — away from the owner. The signing
               CTA travels to this card instead, via the mustSign prop. */
            (axesOf(p)?.approval ?? p.approval_status) !== "approved" ? (
              <PendingPropertyCard
                key={p.id}
                propertyId={p.id}
                title={p.title}
                city={p.city}
                approvalStatus={p.approval_status as "pending" | "rejected"}
                mustSign={ownerMustSign(axesOf(p))}
                submittedAt={p.submitted_at}
                reviewNote={p.review_note}
              />
            ) : (
              <OwnerPropertyCard
                key={p.id}
                property={p}
                yieldPct={yieldByPropertyId.get(p.id) ?? null}
                onboarded={false}
                // in_review is waiting on CoLiviq; everything else here is
                // waiting on the owner (20261012085005).
                pendingState={
                  ownerOnboardingInReview(axesOf(p))
                    ? "in_review"
                    : "awaiting_contract"
                }
              />
            )
          )}
        </div>
      )}
    </AdminV2Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Staff / admin view  (unchanged from original)
// ─────────────────────────────────────────────────────────────────────────────
function AdminPropertiesView() {
  const { t } = useLanguage();
  // Who may decide on a submission. Same gate the approvals section on Owners &
  // access applies, so putting the queue here does not widen who can act on it.
  const { isSuperAdmin, hasSalesAdminRole } = useAuth();
  const canManageApprovals = isSuperAdmin || hasSalesAdminRole;
  const [search, setSearch] = useState("");
  const [city, setCity] = useState<string | null>(null);
  const [view, setView] = useViewMode("properties-list", "grid");
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useProperties();
  const { data: lessors } = useLessors();
  // Only pull the portfolio-wide apartments/rooms/tenants index once the
  // operator actually types a search term.
  const searchIndex = useGlobalSearchIndex(search.trim().length > 0);
  const nestedMatchIds = useNestedMatchPropertyIds(
    searchIndex,
    search.trim().toLowerCase()
  );
  /**
   * Who submitted the properties still awaiting review. The nav badge says how
   * many there are; this is what says WHICH — a count with nothing to click is
   * a nag, not a notification.
   */
  const { data: submissions } = useAdminPropertySubmissions();
  const submitterByPropertyId = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of submissions ?? []) {
      if (s.approval_status !== "pending") continue;
      m.set(Number(s.id), s.owner_name ?? s.owner_email ?? "an owner");
    }
    return m;
  }, [submissions]);
  const all = useMemo(() => data ?? [], [data]);

  // City/search match, BEFORE the overdue/outstanding-rent sort — the stats
  // batches below only need to cover whatever a filter has already narrowed
  // the list to, not the whole portfolio.
  const matched = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all.filter((p) => {
      if (city && p.city !== city) return false;
      if (!term) return true;
      return (
        (p.title ?? "").toLowerCase().includes(term) ||
        (p.address ?? "").toLowerCase().includes(term) ||
        (p.city ?? "").toLowerCase().includes(term) ||
        (nestedMatchIds?.has(String(p.id)) ?? false)
      );
    });
  }, [all, search, city, nestedMatchIds]);

  const matchedPropertyIds = useMemo(() => matched.map((p) => p.id), [matched]);
  // Outstanding-rent % must be known for every MATCHED property BEFORE
  // pagination/sort, so this is one batched fetch (scoped to the current
  // filter, not the whole portfolio) rather than each PropertyCard's own
  // lazy per-property query. The result is passed down as a prop (see
  // PropertyCard's rentPaidStats prop) so a card never ALSO fires its own
  // per-property fetch for the same data.
  const { data: rentStatsById, isError: rentStatsError } =
    useAllPropertiesRentPaidStats(matchedPropertyIds);

  const outstandingPctById = useMemo(() => {
    const m = new Map<number, number | null>();
    for (const p of matched) {
      const stats = rentStatsById?.get(p.id);
      m.set(
        p.id,
        !stats || stats.activeTenancies === 0 || stats.pct == null
          ? null
          : 100 - stats.pct
      );
    }
    return m;
  }, [matched, rentStatsById]);

  // Overdue-reminder totals (the card's reminders badge) for every MATCHED
  // property, for the same reason: they outrank outstanding rent in the sort,
  // so they must be known before pagination. Same query as each card's own
  // badge, so the cards read this cache rather than fetching again. `undefined`
  // until every property has settled — see useAllPropertiesReminderCounts.
  const reminderCounts = useAllPropertiesReminderCounts(matched);
  const overdueCountById = useMemo(
    () =>
      new Map(
        Object.entries(reminderCounts ?? {}).map(([id, n]) => [Number(id), n])
      ),
    [reminderCounts]
  );

  const lessorNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of lessors ?? []) m.set(l.id, l.name);
    return m;
  }, [lessors]);

  /**
   * Owner label for a property card, in order of how definite the answer is:
   * the property's own lessor, then the lessors on its apartments, then the
   * owner ACCOUNTS on those apartments.
   *
   * Most buildings carry no property-level lessor because ownership sits on
   * the apartments. And an owner-submitted building carries no lessor at all —
   * a lessor is a billing entity staff create after approval, while
   * `apartments.owner_user_id` is set at submission time. Without that last
   * step the card read "No owner" for a property whose owner had just filled
   * in the form. "No owner" now means what it says: nobody on record anywhere.
   */
  const ownerLabelFor = useCallback(
    (p: PropertyListItem): string | null => {
      if (p.lessor_id) return lessorNameById.get(p.lessor_id) ?? null;
      const lessorNames = p.apartment_lessor_ids
        .map((id) => lessorNameById.get(id))
        .filter((n): n is string => !!n);
      const names = lessorNames.length
        ? lessorNames
        : (p.apartment_owner_names ?? []);
      if (names.length === 0) return null;
      if (names.length === 1) return names[0];
      // Several owners in one building; the Apartments tab breaks them down.
      return `${names.length} owners`;
    },
    [lessorNameById]
  );

  const totals = useMemo(() => {
    const visible = all.filter((p) => !p.is_hidden);
    const totalRooms = visible.reduce((n, p) => n + p.rooms_count, 0);
    const occupied = visible.reduce((n, p) => n + p.occupied_count, 0);
    const apartments = visible.reduce((n, p) => n + p.apartments_count, 0);
    const withApartments = visible.filter((p) => p.apartments_count > 0).length;
    return {
      propertyCount: visible.length,
      totalRooms,
      occupied,
      apartments,
      withApartments,
    };
  }, [all]);

  const cityOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of all) {
      if (!p.city) continue;
      counts.set(p.city, (counts.get(p.city) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, label: value, count }));
  }, [all]);

  const filtered = useMemo(
    () =>
      [...matched].sort((a, b) =>
        compareByOverdueThenOutstandingRent(
          a,
          b,
          outstandingPctById,
          overdueCountById
        )
      ),
    [matched, outstandingPctById, overdueCountById]
  );

  const PAGE_SIZE = 25;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  useEffect(() => {
    setPage(1);
  }, [search, city]);

  const isInitialEmpty = !isLoading && all.length === 0;
  const hasFilters = search.trim().length > 0 || city !== null;
  const isFilterEmpty = !isLoading && all.length > 0 && filtered.length === 0;
  const resultLabel = hasFilters ? `${filtered.length} of ${all.length}` : null;

  // Keeps the Approvals rail's top border level with the "All properties"
  // section instead of the KPI strip above it. Measured rather than a fixed
  // offset because the block above (description, stat cards, filter chips)
  // reflows at different viewport widths and city counts.
  const dashboardHeaderRef = useRef<HTMLDivElement>(null);
  const listSectionRef = useRef<HTMLDivElement>(null);
  const [sidebarOffset, setSidebarOffset] = useState(0);

  useLayoutEffect(() => {
    const header = dashboardHeaderRef.current;
    const listSection = listSectionRef.current;
    if (!header || !listSection) return;

    const measure = () => {
      const offset =
        listSection.getBoundingClientRect().top -
        header.getBoundingClientRect().top;
      setSidebarOffset(Math.max(0, Math.round(offset)));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    observer.observe(listSection);
    return () => observer.disconnect();
  }, [
    isLoading,
    isInitialEmpty,
    isFilterEmpty,
    hasFilters,
    cityOptions.length,
  ]);

  return (
    <AdminV2Layout
      title={t("admin.nav.properties", "Properties")}
      /* The approvals queue goes in the layout's right column — the 320px
         track every V2 page already reserves and this one was leaving empty.
         A building waiting to become real belongs beside the buildings, not
         filed a page away under the person who sent it; putting it in the
         column that was already there means the property cards keep every
         pixel they had. The rail is pushed down by sidebarOffset so its top
         border lines up with the property list below the KPI/search strip,
         not the strip itself. */
      sidebar={
        canManageApprovals ? (
          <div
            className="lg:mt-[var(--sidebar-offset)]"
            style={
              {
                "--sidebar-offset": `${sidebarOffset}px`,
              } as React.CSSProperties
            }
          >
            <PropertyApprovalsRail />
          </div>
        ) : undefined
      }
      actions={
        <Button asChild className="rounded-xl shadow-sm">
          <Link to="/admin/properties/new">
            <Plus className="mr-2 h-4 w-4" />
            {t("admin.properties.addProperty", "Add property")}
          </Link>
        </Button>
      }
    >
      <div ref={dashboardHeaderRef}>
        {/* Premium dashboard surface */}
        <div className="relative">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-24 -left-16 h-64 w-64 rounded-full bg-emerald-400/12 blur-3xl" />
            <div className="absolute -top-20 right-0 h-72 w-72 rounded-full bg-sky-400/12 blur-3xl" />
            <div className="absolute top-28 left-1/2 -translate-x-1/2 h-64 w-64 rounded-full bg-violet-400/8 blur-3xl" />
          </div>

          <div className="mb-6">
            <p className="text-sm text-muted-foreground max-w-2xl">
              {t(
                "admin.properties.dashboardHint",
                "Manage your portfolio, jump into room and reminder workflows, and monitor occupancy at a glance."
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard
              label={t("admin.nav.properties", "Properties")}
              value={totals.propertyCount}
              hint={
                isLoading
                  ? undefined
                  : `${all.filter((p) => p.is_hidden).length} ${t("common.hidden", "hidden").toLowerCase()}`
              }
              icon={Home}
              loading={isLoading}
              className="bg-gradient-to-br from-muted/10 to-background/80"
            />
            <StatCard
              label={t("admin.field.apartments", "Apartments")}
              value={totals.apartments}
              hint={
                isLoading
                  ? undefined
                  : t("admin.field.ownerOwnedUnits", "owner-owned units")
              }
              icon={Building}
              loading={isLoading}
              className="bg-gradient-to-br from-teal-50/60 to-background/80 border-teal-200/40"
            />
            <StatCard
              label={t("admin.field.totalRooms", "Total rooms")}
              value={totals.totalRooms}
              icon={BedDouble}
              loading={isLoading}
              className="bg-gradient-to-br from-sky-50/60 to-background/80 border-sky-200/40"
            />
            <StatCard
              label={t("admin.field.occupied", "Occupied")}
              value={totals.occupied}
              hint={
                !isLoading && totals.totalRooms > 0
                  ? `${Math.round((totals.occupied / totals.totalRooms) * 100)}% ${t("admin.properties.ofRooms", "of rooms")}`
                  : undefined
              }
              icon={CheckCircle2}
              loading={isLoading}
              className="bg-gradient-to-br from-emerald-50/60 to-background/80 border-emerald-200/40"
            />
          </div>
        </div>

        {error && (
          <Card className="p-4 mb-4 border-destructive bg-destructive/5">
            <p className="text-sm font-medium">
              {t(
                "admin.error.couldNotLoadProperties",
                "Could not load properties"
              )}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {error instanceof Error
                ? error.message
                : t("common.unknownError", "Unknown error")}
            </p>
          </Card>
        )}

        {!isInitialEmpty && (
          <Card className="mb-6 border-border/60 bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/60 rounded-2xl sticky top-14 z-20">
            <div className="p-4 sm:p-5 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px] max-w-2xl">
                  {/* Same categorized apartment/room/tenant dropdown as the
                      property-detail page's global search, plus a
                      "Properties" group here. Its debounced value also
                      drives the grid filter below, via `search`. */}
                  <PropertyGlobalSearch
                    showPropertyGroup
                    value={search}
                    onDebouncedChange={setSearch}
                    placeholder={t(
                      "admin.field.searchByNameAddressCity",
                      "Search by name, address, or city…"
                    )}
                    inputId="properties-search"
                    trailing={resultLabel}
                  />
                </div>
                <ViewToggle value={view} onChange={setView} />
              </div>
              {cityOptions.length > 1 && (
                <FilterChipBar
                  options={cityOptions}
                  value={city}
                  onChange={setCity}
                  allLabel={`${t("admin.properties.allCities", "All cities")} (${all.length})`}
                />
              )}
            </div>
          </Card>
        )}
      </div>

      <div ref={listSectionRef}>
        {isLoading && view === "grid" && (
          <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="aspect-[16/10] w-full rounded-none" />
                <div className="p-4 space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                  <div className="flex gap-2 pt-1">
                    <Skeleton className="h-5 w-16 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
        {isLoading && view === "list" && (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="p-3 flex items-center gap-4">
                <Skeleton className="h-16 w-24 rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-8 w-16 hidden sm:block" />
                <Skeleton className="h-8 w-16 hidden sm:block" />
              </Card>
            ))}
          </div>
        )}

        {isInitialEmpty && (
          <Card className="border-dashed">
            <EmptyState
              icon={Building2}
              title={t("admin.empty.noPropertiesTitle", "No properties yet")}
              description={t(
                "admin.empty.noPropertiesDesc",
                "Use the wizard to add your first property — it walks you through lessor selection, lease terms, and mass-creating rooms."
              )}
              actions={
                <Button asChild>
                  <Link to="/admin/properties/new">
                    <Plus className="mr-2 h-4 w-4" />
                    {t(
                      "admin.properties.addFirstProperty",
                      "Add your first property"
                    )}
                  </Link>
                </Button>
              }
            />
          </Card>
        )}

        {isFilterEmpty && (
          <Card className="border-dashed">
            <EmptyState
              title={t(
                "admin.empty.noPropertiesMatch",
                "No properties match your filters"
              )}
              description={
                search && city
                  ? t(
                      "admin.properties.noMatchInCity",
                      'No matches for "{{search}}" in {{city}}.'
                    )
                      .replace("{{search}}", search)
                      .replace("{{city}}", city)
                  : search
                    ? t(
                        "admin.properties.noMatchFor",
                        'No matches for "{{search}}".'
                      ).replace("{{search}}", search)
                    : t(
                        "admin.properties.noPropertiesInCity",
                        "No properties in {{city}}."
                      ).replace("{{city}}", city ?? "")
              }
              actions={
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSearch("");
                    setCity(null);
                  }}
                >
                  {t("common.clearFilters", "Clear filters")}
                </Button>
              }
              compact
            />
          </Card>
        )}

        {!isLoading && filtered.length > 0 && (
          <Card className="border-border/60 bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/60 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-4 sm:px-5 py-3 border-b border-border/60 flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-foreground">
                {hasFilters
                  ? t(
                      "admin.properties.matchingProperties",
                      "Matching properties"
                    )
                  : t("admin.properties.allProperties", "All properties")}
                <span className="ml-2 text-xs font-semibold text-muted-foreground tabular-nums">
                  ({filtered.length})
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                {t(
                  "admin.properties.clickCardToOpen",
                  "Click a card to open details"
                )}
              </div>
            </div>
            <div className="p-4 sm:p-5">
              {view === "grid" ? (
                <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {pageItems.map((p) => (
                    <PropertyCard
                      key={p.id}
                      property={p}
                      submittedByName={submitterByPropertyId.get(p.id) ?? null}
                      rentPaidStats={{
                        data: rentStatsById?.get(p.id),
                        isError: rentStatsError,
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {pageItems.map((p) => (
                    <PropertyListRow
                      key={p.id}
                      property={p}
                      lessorName={ownerLabelFor(p)}
                    />
                  ))}
                </div>
              )}
            </div>

            {filtered.length > PAGE_SIZE ? (
              <div className="px-4 sm:px-5 py-4 border-t border-border/60 bg-muted/20 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-muted-foreground">
                  {t("common.showing", "Showing")}{" "}
                  <span className="font-medium text-foreground tabular-nums">
                    {(safePage - 1) * PAGE_SIZE + 1}–
                    {Math.min(safePage * PAGE_SIZE, filtered.length)}
                  </span>{" "}
                  {t("common.of", "of")}{" "}
                  <span className="font-medium text-foreground tabular-nums">
                    {filtered.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl h-8"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    {t("common.prev", "Prev")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl h-8"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    {t("common.next", "Next")}
                  </Button>
                </div>
              </div>
            ) : null}
          </Card>
        )}
      </div>
    </AdminV2Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page entry — routes to the correct view based on role
// ─────────────────────────────────────────────────────────────────────────────
export default function V2PropertiesList() {
  const { isOwnerPortalOnly } = useAuth();
  return isOwnerPortalOnly ? <OwnerPropertiesView /> : <AdminPropertiesView />;
}
